// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo } from 'react';
import "./EventCountAnalytics.scss";
import { useTranslation } from "react-i18next";
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export default function EventCountAnalytics({ logs, t, companies, getCompanyDisplayName }) {
  const { t: translate } = useTranslation();
  const tFunction = t || translate;

  // Process logs to extract event counts data
  const processedData = useMemo(() => {
    if (!logs || logs.length === 0) {
      return {
        totalEvents: 0,
        eventCounts: {},
        eventsByDay: {},
        eventsByHour: Array(24).fill(0),
        topEvents: []
      };
    }

    // Count events by name
    const eventCounts = {};
    logs.forEach(log => {
      const eventName = log.event_name || 'unknown';
      eventCounts[eventName] = (eventCounts[eventName] || 0) + 1;
    });

    // Sort events by count descending
    const topEvents = Object.entries(eventCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    // Count events by day
    const eventsByDay = {};
    logs.forEach(log => {
      const date = log.timestamp.toDate();
      const dayKey = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      if (!eventsByDay[dayKey]) {
        eventsByDay[dayKey] = {
          date: dayKey,
          count: 0,
          events: {}
        };
      }
      
      eventsByDay[dayKey].count++;
      
      const eventName = log.event_name || 'unknown';
      eventsByDay[dayKey].events[eventName] = (eventsByDay[dayKey].events[eventName] || 0) + 1;
    });

    // Count events by hour
    const eventsByHour = Array(24).fill(0);
    logs.forEach(log => {
      const hour = log.timestamp.toDate().getHours();
      eventsByHour[hour]++;
    });

    return {
      totalEvents: logs.length,
      eventCounts,
      eventsByDay,
      eventsByHour,
      topEvents
    };
  }, [logs]);

  // Prepare data for top events chart (doughnut chart)
  const topEventsChartData = useMemo(() => {
    // Take top 7 events, group the rest as "Other"
    const TOP_EVENTS_COUNT = 7;
    const topEvents = processedData.topEvents.slice(0, TOP_EVENTS_COUNT);
    
    // Calculate "Other" events count if there are more than TOP_EVENTS_COUNT
    let otherCount = 0;
    if (processedData.topEvents.length > TOP_EVENTS_COUNT) {
      otherCount = processedData.topEvents
        .slice(TOP_EVENTS_COUNT)
        .reduce((sum, event) => sum + event.count, 0);
    }
    
    // Colors for chart
    const backgroundColors = [
      '#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6', '#1abc9c', '#34495e'
    ];
    
    // Labels and data
    const labels = topEvents.map(event => event.name);
    const data = topEvents.map(event => event.count);
    
    // Add "Other" if needed
    if (otherCount > 0) {
      labels.push(tFunction('other_events') || 'Other');
      data.push(otherCount);
      backgroundColors.push('#95a5a6');
    }
    
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors,
          borderColor: '#ffffff',
          borderWidth: 2,
        }
      ]
    };
  }, [processedData.topEvents, tFunction]);

  // Prepare data for events by hour chart
  const hourlyChartData = useMemo(() => {
    return {
      labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
      datasets: [
        {
          label: tFunction('events_by_hour') || 'Events by Hour',
          data: processedData.eventsByHour,
          backgroundColor: '#3498db',
          borderColor: '#2980b9',
          borderWidth: 1
        }
      ]
    };
  }, [processedData.eventsByHour, tFunction]);

  // Prepare data for events by day chart
  const dailyChartData = useMemo(() => {
    // Get last 14 days of data
    const days = Object.keys(processedData.eventsByDay).sort();
    const recentDays = days.slice(-14);
    
    const labels = recentDays.map(day => {
      const date = new Date(day);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    });
    
    const data = recentDays.map(day => processedData.eventsByDay[day]?.count || 0);
    
    return {
      labels,
      datasets: [
        {
          label: tFunction('events_by_day') || 'Events by Day',
          data,
          backgroundColor: '#2ecc71',
          borderColor: '#27ae60',
          borderWidth: 1
        }
      ]
    };
  }, [processedData.eventsByDay, tFunction]);

  // Chart options
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#24292e',
        bodyColor: '#24292e',
        borderColor: '#eaecef',
        borderWidth: 1,
        padding: 12
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };
  
  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#24292e',
        bodyColor: '#24292e',
        borderColor: '#eaecef',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: function(context) {
            const value = context.raw;
            const total = context.dataset.data.reduce((acc, curr) => acc + curr, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Empty state handling
  if (processedData.totalEvents === 0) {
    return (
      <div className="event-count-analytics empty-state">
        <h3>{tFunction('event_count_analytics') || 'Event Count Analytics'}</h3>
        <p>{tFunction('no_events_data') || 'No events data available for the selected logs.'}</p>
      </div>
    );
  }

  return (
    <div className="event-count-analytics">
      <h3>{tFunction('event_count_analytics') || 'Event Count Analytics'}</h3>
      
      <div className="event-count-summary">
        <div className="summary-item">
          <div className="summary-label">{tFunction('total_events') || 'Total Events'}</div>
          <div className="summary-value">{processedData.totalEvents}</div>
        </div>
        <div className="summary-item">
          <div className="summary-label">{tFunction('unique_event_types') || 'Unique Event Types'}</div>
          <div className="summary-value">{processedData.topEvents.length}</div>
        </div>
        <div className="summary-item">
          <div className="summary-label">{tFunction('top_event') || 'Top Event'}</div>
          <div className="summary-value">
            {processedData.topEvents.length > 0 ? processedData.topEvents[0].name : '-'}
          </div>
        </div>
      </div>
      
      <div className="charts-container">
        <div className="top-events-chart">
          <h4>{tFunction('event_distribution') || 'Event Distribution'}</h4>
          <div className="chart-wrapper">
            <Doughnut data={topEventsChartData} options={doughnutChartOptions} />
          </div>
        </div>
        
        <div className="events-by-time">
          <div className="daily-chart">
            <h4>{tFunction('events_by_day') || 'Events by Day'}</h4>
            <div className="chart-wrapper">
              <Bar data={dailyChartData} options={barChartOptions} />
            </div>
          </div>
          
          <div className="hourly-chart">
            <h4>{tFunction('events_by_hour') || 'Events by Hour of Day'}</h4>
            <div className="chart-wrapper">
              <Bar data={hourlyChartData} options={barChartOptions} />
            </div>
          </div>
        </div>
        
        <div className="top-events-table">
          <h4>{tFunction('top_events') || 'Top Events'}</h4>
          <table>
            <thead>
              <tr>
                <th>{tFunction('event_name') || 'Event Name'}</th>
                <th>{tFunction('count') || 'Count'}</th>
                <th>{tFunction('percentage') || '%'}</th>
              </tr>
            </thead>
            <tbody>
              {processedData.topEvents.slice(0, 10).map((event, index) => (
                <tr key={index} className={index === 0 ? 'top-event' : ''}>
                  <td className="event-name">{event.name}</td>
                  <td className="event-count">{event.count}</td>
                  <td className="event-percentage">
                    {((event.count / processedData.totalEvents) * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 